// ============================================================================
// JARVISTRAVEL - LOADER SCREEN
//
// The first screen anyone sees, and the last part of the app still running the
// pre-Meridian brand: a blue-to-purple gradient, an invented plane glyph in a
// white tile, pure white type, and a black bottom fade. Every one of those is
// something the brand guide rules out.
//
// Now the chart room. Ateneo ground, the real lockup, Moonlight type, Sea
// Buckthorn on the progress fill, and contour rings with a single amber sun as
// the only decoration.
//
// Mode-invariant on purpose. This paints before the theme has resolved, so a
// light/dark split here would flash; the chart-room register is one navy for
// both schemes, which is also what the waitlist and the unfurl cards use.
// ============================================================================

import { useEffect, useState } from 'react';
import { APP_CONFIG, ANIMATIONS } from '@/app/constants';
import { LogoLockup } from '@/app/components/shared/Logo';

interface LoaderScreenProps {
  onComplete: () => void;
  duration?: number;
}

export function LoaderScreen({ onComplete, duration = ANIMATIONS.loader }: LoaderScreenProps) {
  const [progress, setProgress] = useState(0);
  
  useEffect(() => {
    const increment = 100 / (duration / 50);
    
    const interval = setInterval(() => {
      setProgress(prev => {
        const next = prev + increment + Math.random() * 2;
        if (next >= 100) {
          clearInterval(interval);
          setTimeout(onComplete, 300);
          return 100;
        }
        return next;
      });
    }, 50);
    
    return () => clearInterval(interval);
  }, [duration, onComplete]);
  
  return (
    <div className="fixed inset-0 bg-ateneo flex flex-col items-center justify-center overflow-hidden">
      {/* Chart-room contours: the sun's halo drawn as chart rings, which is the
          brand's only sanctioned decoration. They replace six floating white
          orbs on a gradient — a gradient the guide bans outright.

          ONE animated wrapper holding both layers. They were two siblings with
          `animate-drift` on the rings alone, so the drift keyframes moved the
          rings up 14px and rotated them 2° while the sun stayed put — at peak
          drift the sun sat about its own radius off the ring it is supposed to
          be plotted on, which is the opposite of what the comment claimed
          (review). The sun only stays on the ring if the two move together.

          Colours are the brand tokens, not the hex values they resolve to: a
          palette change must reach the loader, and `stroke-moonlight` /
          `fill-buckthorn` cannot drift out of sync the way `#F0EEEB` and
          `#FFBF65` silently could. */}
      <div
        className="absolute -top-[42vmin] -right-[30vmin] w-[135vmin] h-[135vmin] animate-drift"
        aria-hidden="true"
      >
        <svg viewBox="0 0 1000 1000" className="w-full h-full">
          {[300, 400, 497].map((r) => (
            <circle
              key={r}
              cx="500"
              cy="500"
              r={r}
              fill="none"
              className="stroke-moonlight"
              strokeOpacity="0.13"
              strokeWidth="2"
            />
          ))}
          {/* One solid amber sun, plotted on the inner ring — same coordinate
              space, same transform, so it stays there. */}
          <circle cx="200" cy="500" r="11" className="fill-buckthorn" />
        </svg>
      </div>

      <div className="relative z-10 text-center px-6">
        {/* The real lockup, inlined so fill="currentColor" picks up Moonlight
            from the wrapper. No tile: containment is option B, the mark stands
            free in clear space. */}
        <div className="text-moonlight animate-rise">
          {/* No em dash in the accessible name: the guide bans them, and this
              string is read aloud. */}
          <LogoLockup
            className="h-14 sm:h-16 w-auto mx-auto"
            title={`${APP_CONFIG.name}, loading`}
          />
        </div>

        <p className="text-moonlight/70 text-lg mt-6 mb-10">{APP_CONFIG.tagline}</p>

        <div className="w-64 mx-auto">
          {/* A bar, not a badge — round ends are correct here. */}
          <div className="h-1.5 bg-moonlight/20 rounded-full overflow-hidden">
            <div
              className="h-full bg-buckthorn rounded-full transition-all duration-100 ease-out"
              style={{ width: `${progress}%` }}
              role="progressbar"
              aria-valuenow={Math.round(progress)}
              aria-valuemin={0}
              aria-valuemax={100}
              aria-label="Loading"
            />
          </div>
          <p className="text-moonlight/60 text-sm mt-3" aria-live="polite">
            {progress < 30 && 'Getting your bearings'}
            {progress >= 30 && progress < 60 && 'Pulling in your trips'}
            {progress >= 60 && progress < 90 && 'Checking the pacing'}
            {progress >= 90 && 'Ready when you are'}
          </p>
        </div>
      </div>

      <style>{`
        @keyframes drift {
          0%, 100% { transform: translateY(0) rotate(0deg); }
          50%      { transform: translateY(-14px) rotate(2deg); }
        }
        @keyframes rise {
          from { opacity: 0; transform: translateY(8px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .animate-drift { animation: drift 18s ease-in-out infinite; }
        .animate-rise  { animation: rise .5s ease-out both; }

        /* The whole screen is decorative motion over a static brand mark, so it
           has nothing to say to someone who asked for less of it. */
        @media (prefers-reduced-motion: reduce) {
          .animate-drift, .animate-rise { animation: none; }
        }
      `}</style>
    </div>
  );
}

export default LoaderScreen;
